import { stringValidation } from './stringValidation';

import type { FieldError, FieldOptions } from '../types';

export function validator<CustomValidatorArguments extends unknown[] = unknown[]>(
  options: FieldOptions<CustomValidatorArguments>,
  value: unknown,
): FieldError {
  const { type, required, maxLength, minLength, maxValue, minValue, regex, child, children, customValidation } =
    options;

  if (customValidation) {
    const { customValidator, validationArgs } = customValidation;

    const customValidatorReturn = customValidator(value, ...validationArgs);
    if (customValidatorReturn) return customValidatorReturn;
  }

  // 0 or false are valid values, null, undefined or empty string not
  const isValue = value !== undefined && value !== null && value !== '';

  if (required && !isValue) {
    return 'This field  is required';
  }

  if (isValue) {
    // check types
    if (type === 'boolean' && !(value === true || value === false)) {
      return 'not a boolean';
    }
    if ((type === 'number' || type === 'float') && !Number.isFinite(value)) {
      return 'not a number';
    }
    if (type === 'list' && !Array.isArray(value)) {
      return 'not an array';
    }
    if (type === 'list' && typeof value !== 'object') {
      return 'not a object';
    }

    // validate all string types
    if (
      type === 'alphaNumericDash' ||
      type === 'url' ||
      type === 'httpUrl' ||
      type === 'string' ||
      regex ||
      type === 'enum'
    ) {
      if (typeof value !== 'string') return 'not a string';

      const error = stringValidation(options, value);
      if (error) return error;
    }

    if (maxValue && typeof value === 'number' && value > maxValue) {
      return `Exceeds max value of ${maxValue}`;
    }
    if (minValue && typeof value === 'number' && value < minValue) {
      return `Exceeds min value of ${minValue}`;
    }

    if (type === 'list' && Array.isArray(value) && child) {
      if (maxLength && value.length > maxLength) {
        return `Exceeds max length of ${maxLength}`;
      }
      if (minLength && value.length < minLength) {
        return `Select a minimum of ${minLength}`;
      }

      return value
        .map((item) => {
          if (item) return validator(child, item);
          return undefined;
        })
        .find((error) => !!error);
    }

    // validator for object. all key/value pairs should be valid
    if (type === 'object' && typeof value === 'object' && children) {
      return Object.keys(value)
        .map((key) => {
          if (children[key]) {
            // @ts-ignore
            return validator(children[key], value[key]);
          }
          return undefined; // no validator for key
        })
        .find((error) => !!error); // return first found error
    }
  }

  return undefined; // nothing to complain about
}
